/**
 * Libellés français et présentation des gammes d'entretien.
 *
 * Comme pour la conformité et les machines, la traduction des codes de l'API
 * vit en un seul endroit : la carte de la fiche machine et l'écran « Gammes »
 * doivent dire la même chose du même palier.
 */
import type {
  AvertissementReferenceGamme,
  EcheanceTheorique,
  GammeLigne,
  GammeNiveau,
  GammeSeuil,
  GenreLigneGamme,
  StatutVersionGamme,
} from '../api/client';

const FORMAT_NOMBRE = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 3 });
const FORMAT_PRIX = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });

export function nombre(n: number): string {
  return FORMAT_NOMBRE.format(n);
}

/** « — » quand il n'y a pas de prix ; jamais « 0 € » inventé. */
export function prix(montant: number | null | undefined): string {
  return montant === null || montant === undefined ? '—' : FORMAT_PRIX.format(montant);
}

/** Quantité inconnue = « — » (défaut d'export constructeur), jamais « 1 ». */
export function quantite(q: number | null, unite: string | null): string {
  if (q === null) return '—';
  return unite ? `${nombre(q)} ${unite}` : nombre(q);
}

/**
 * Un seuil en mots : « tous les 6 000 h, le premier à 3 000 h »,
 * « une fois par an », « une seule fois à 15 000 h ».
 */
export function decrireSeuil(seuil: GammeSeuil): string {
  const unite = seuil.dimension === 'HOURS' ? 'h' : 'mois';
  if (seuil.interval === null) {
    return `une seule fois à ${nombre(seuil.first)} ${unite}`;
  }
  if (seuil.dimension === 'MONTHS' && seuil.first === 12 && seuil.interval === 12) {
    return 'une fois par an';
  }
  if (seuil.first === seuil.interval) {
    return `tous les ${nombre(seuil.interval)} ${unite}`;
  }
  return `tous les ${nombre(seuil.interval)} ${unite}, le premier à ${nombre(seuil.first)} ${unite}`;
}

/** Un palier peut porter heures ET mois : « 4 000 h ou 1 an » se lit « … ou … ». */
export function decrireNiveau(niveau: GammeNiveau): string {
  return niveau.thresholds.map(decrireSeuil).join(' ou ');
}

/**
 * Le temps de main d'œuvre qu'un palier prévoit, en une phrase : « main d'œuvre prévue : 3 h par technicien » ; `null` quand il n'est
 * pas renseigné (lot B23). Du temps, jamais un prix : tout le monde qui lit la gamme le voit.
 */
export function phraseTempsMo(minutes: number | null | undefined): string | null {
  if (minutes === null || minutes === undefined || minutes <= 0) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const duree = h === 0 ? `${m} min` : m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')}`;
  return `main d’œuvre prévue : ${duree} par technicien`;
}

/** « 3 000 h · A   6 000 h · B … » — les premiers points de la grille. */
export function resumerEcheances(
  echeances: readonly EcheanceTheorique[],
  unite: 'h' | 'mois',
  max = 12,
): string {
  const debut = echeances
    .slice(0, max)
    .map((e) => `${nombre(e.valeur)} ${unite} (${e.code})`)
    .join(' · ');
  return echeances.length > max ? `${debut} …` : debut;
}

export const GENRE_LIGNE: Record<GenreLigneGamme, { texte: string; classe: string }> = {
  REPLACE: { texte: 'Remplacement', classe: 'actif' },
  CHECK: { texte: 'Contrôle', classe: 'attente' },
  CONDITIONAL: { texte: 'Si nécessaire', classe: 'suspendu' },
};

export const STATUT_VERSION: Record<StatutVersionGamme, { texte: string; classe: string }> = {
  BROUILLON: { texte: 'Brouillon', classe: 'attente' },
  PUBLIEE: { texte: 'Publiée', classe: 'actif' },
  ARCHIVEE: { texte: 'Archivée', classe: 'revoque' },
};

/**
 * Les clés de prix sont ABSENTES de la réponse quand l'appelant n'a pas le droit
 * de les voir : on teste leur PRÉSENCE, pas leur valeur.
 */
export function ligneAvecPrix(ligne: GammeLigne): boolean {
  return 'publicPrice' in ligne || 'internalPrice' in ligne;
}

export function niveauxAvecPrix(niveaux: readonly GammeNiveau[]): boolean {
  return niveaux.some((n) => n.lines.some(ligneAvecPrix));
}

export function niveauxAvecEquivalents(niveaux: readonly GammeNiveau[]): boolean {
  return niveaux.some((n) => n.lines.some((l) => l.adaptableReference !== null));
}

/** « 101253.1 ou 101253.2 » : les références pour lesquelles une gamme a été établie. */
export function listerReferences(references: readonly string[]): string {
  return references.join(' ou ');
}

/**
 * Ce que l'écran dit quand la référence constructeur d'une machine n'est pas celle pour
 * laquelle la gamme a été établie. Même phrase sur la fiche (suggestion ou gamme rattachée)
 * et dans la confirmation avant de rattacher.
 */
export function decrireAvertissementReference(
  a: AvertissementReferenceGamme,
  options: { readonly dejaRattachee?: boolean } = {},
): string {
  const gamme = listerReferences(a.referencesGamme);
  if (a.type === 'ABSENTE') {
    return (
      `Cette gamme est établie pour la référence constructeur ${gamme}. La référence de cette machine ` +
      'n’est pas renseignée : vérifiez qu’il s’agit bien de la même variante.'
    );
  }
  const conseil = options.dejaRattachee
    ? 'vérifiez que ses pièces conviennent à cette machine.'
    : 'vérifiez avant de la rattacher.';
  return (
    `Cette gamme est établie pour la référence constructeur ${gamme} ; cette machine est en ` +
    `${a.referenceMachine ?? '?'}. Les pièces d’une autre variante peuvent différer : ${conseil}`
  );
}

/** Date ISO « 2026-10-06 » → « 06/10/2026 ». */
export function dateFr(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}
